<!-- components/InstallPrompt.vue -->
<template>
  <!-- Prompt Principal -->
  <Transition name="slide-up" appear>
    <div
        v-if="store.showPrompt && !isInstalling"
        class="fixed bottom-4 left-4 right-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 z-50 max-w-sm mx-auto"
    >
      <div class="flex items-start space-x-3">
        <!-- Icône avec indicateur -->
        <div class="relative w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon
              :name="promptIcon"
              class="w-6 h-6"
              :class="promptIconColor"
          />

          <!-- Badge de mise à jour -->
          <div
              v-if="store.needRefresh"
              class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center animate-pulse"
          >
            <span class="text-xs text-white font-bold">!</span>
          </div>
        </div>

        <!-- Contenu -->
        <div class="flex-1 min-w-0">
          <h4 class="font-semibold text-gray-900 dark:text-white text-base">
            {{ promptTitle }}
          </h4>

          <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
            {{ promptDescription }}
          </p>

          <!-- Info navigateur -->
          <div
              v-if="showBrowserInfo && store.browserSupport.browserName"
              class="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center space-x-1"
          >
            <Icon name="heroicons:information-circle" class="w-3 h-3" />
            <span>{{ browserInfoText }}</span>
          </div>

          <!-- Statut offline -->
          <div
              v-if="store.offlineReady && !store.needRefresh"
              class="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center space-x-1"
          >
            <Icon name="heroicons:check-circle" class="w-3 h-3" />
            <span>{{ $t('pwa.status.offline_ready') }}</span>
          </div>

          <!-- Boutons d'action -->
          <div class="flex space-x-2 mt-3">
            <button
                @click="handleMainAction"
                :disabled="isInstalling"
                class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2"
                :class="mainButtonClasses"
            >
              <Icon
                  v-if="isInstalling"
                  name="heroicons:arrow-path"
                  class="w-4 h-4 animate-spin"
              />
              <span>{{ mainButtonText }}</span>
            </button>

            <button
                @click="handleDismiss"
                :disabled="isInstalling"
                class="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200"
            >
              {{ $t('pwa.action.later') }}
            </button>
          </div>
        </div>

        <!-- Bouton fermer -->
        <button
            @click="handleDismiss"
            :disabled="isInstalling"
            class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50 p-1 rounded-md transition-colors duration-200"
            :aria-label="$t('common.close')"
        >
          <Icon name="heroicons:x-mark" class="w-5 h-5" />
        </button>
      </div>
    </div>
  </Transition>

  <!-- Instructions manuelles (Safari/Firefox) -->
  <Transition name="fade">
    <div
        v-if="showManualInstructions"
        class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-4"
        @click.self="closeManualInstructions"
    >
      <div class="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-xl shadow-2xl p-6 w-full sm:max-w-md">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ $t('pwa.manual.title') }}
          </h3>
          <button
              @click="closeManualInstructions"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
          >
            <Icon name="heroicons:x-mark" class="w-6 h-6" />
          </button>
        </div>

        <!-- Instructions -->
        <div v-if="manualInstructions" class="space-y-4">
          <!-- Navigateur détecté -->
          <div class="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
            <Icon :name="manualInstructions.icon" class="w-6 h-6" />
            <span class="text-sm">
              {{ $t('pwa.manual.browser', { browser: store.browserSupport.browserName }) }}
            </span>
          </div>

          <!-- Étapes -->
          <ol class="space-y-3">
            <li
                v-for="(step, index) in manualInstructions.steps"
                :key="index"
                class="flex items-start space-x-3"
            >
              <span class="flex-shrink-0 w-6 h-6 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-sm font-medium">
                {{ index + 1 }}
              </span>
              <span class="text-sm text-gray-700 dark:text-gray-300">{{ step }}</span>
            </li>
          </ol>
        </div>

        <!-- Bouton fermer -->
        <button
            @click="closeManualInstructions"
            class="mt-6 w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200"
        >
          {{ $t('pwa.manual.understood') }}
        </button>
      </div>
    </div>
  </Transition>

  <!-- Notifications Toast -->
  <Transition name="slide-down">
    <div
        v-if="showToast"
        class="fixed top-4 left-4 right-4 z-[70] max-w-sm mx-auto"
    >
      <div
          class="rounded-lg shadow-lg border p-4 flex items-center space-x-3"
          :class="toastClasses"
      >
        <Icon :name="toastIcon" class="w-5 h-5 flex-shrink-0" />
        <span class="text-sm font-medium">{{ toastMessage }}</span>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useInstallPromptStore } from '~/stores/pwa'

// Props
const props = defineProps({
  showBrowserInfo: {
    type: Boolean,
    default: true
  }
})

// i18n
const { t } = useI18n()

// Store PWA
const store = useInstallPromptStore()

// État local
const isInstalling = ref(false)
const showManualInstructions = ref(false)
const showToast = ref(false)
const toastType = ref<'success' | 'error' | 'warning'>('success')
const toastMessage = ref('')

// Computed
const promptIcon = computed(() => {
  if (store.needRefresh) return 'heroicons:arrow-path'
  if (store.installType === 'version_update') return 'heroicons:arrow-up-circle'
  return 'heroicons:arrow-down-tray'
})

const promptIconColor = computed(() => {
  if (store.needRefresh) return 'text-orange-600 dark:text-orange-400'
  if (store.installType === 'version_update') return 'text-blue-600 dark:text-blue-400'
  return 'text-green-600 dark:text-green-400'
})

const promptTitle = computed(() => {
  if (store.needRefresh) return t('pwa.update.title')
  if (store.installType === 'version_update') return t('pwa.update.new_version')
  return t('pwa.install.title')
})

const promptDescription = computed(() => {
  if (store.needRefresh) return t('pwa.update.description')
  if (store.installType === 'version_update') return t('pwa.update.version_description')
  return t('pwa.install.description')
})

const mainButtonText = computed(() => {
  if (isInstalling.value) return t('pwa.action.loading')
  if (store.needRefresh) return t('pwa.action.refresh')
  if (store.installType === 'version_update') return t('pwa.action.update')
  return t('pwa.action.install')
})

const mainButtonClasses = computed(() => {
  const base = 'disabled:opacity-50 disabled:cursor-not-allowed'

  if (store.needRefresh) {
    return `${base} bg-orange-600 hover:bg-orange-700 text-white`
  }
  if (store.installType === 'version_update') {
    return `${base} bg-blue-600 hover:bg-blue-700 text-white`
  }
  return `${base} bg-green-600 hover:bg-green-700 text-white`
})

const browserInfoText = computed(() => {
  const browser = store.browserSupport.browserName
  if (!browser) return ''

  if (store.browserSupport.hasPrompt) {
    return t('pwa.browser.supports_install', { browser })
  }

  if (store.canShowManualInstructions) {
    return t('pwa.browser.manual_install', { browser })
  }

  return ''
})

const manualInstructions = computed(() => {
  return store.getManualInstallInstructions()
})

const toastClasses = computed(() => {
  const base = 'bg-white dark:bg-gray-800'

  switch (toastType.value) {
    case 'success':
      return `${base} border-green-200 dark:border-green-800 text-green-800 dark:text-green-200`
    case 'warning':
      return `${base} border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200`
    case 'error':
      return `${base} border-red-200 dark:border-red-800 text-red-800 dark:text-red-200`
  }
})

const toastIcon = computed(() => {
  switch (toastType.value) {
    case 'success': return 'heroicons:check-circle'
    case 'warning': return 'heroicons:exclamation-triangle'
    case 'error': return 'heroicons:x-circle'
  }
})

// Méthodes
const showToastMessage = (type: 'success' | 'error' | 'warning', message: string, duration = 3000) => {
  toastType.value = type
  toastMessage.value = message
  showToast.value = true

  setTimeout(() => {
    showToast.value = false
  }, duration)
}

const handleMainAction = async () => {
  // Si c'est une mise à jour vite-pwa
  if (store.needRefresh) {
    try {
      isInstalling.value = true
      await store.applyUpdate()
      showToastMessage('success', t('pwa.message.updating'))
    } catch (error) {
      console.error('Erreur mise à jour:', error)
      showToastMessage('error', t('pwa.message.update_failed'))
    } finally {
      isInstalling.value = false
    }
    return
  }

  // Si c'est une installation PWA (Chrome/Edge)
  if (store.deferredPrompt) {
    try {
      isInstalling.value = true
      const result = await store.installPWA()

      if (result.success) {
        showToastMessage('success', t('pwa.message.install_success'))

        // Analytics
        if (window.gtag) {
          window.gtag('event', 'pwa_installed', {
            event_category: 'PWA',
            event_label: 'User installed PWA'
          })
        }
      } else {
        showToastMessage('warning', t('pwa.message.install_cancelled'))
      }
    } catch (error) {
      console.error('Erreur installation:', error)
      showToastMessage('error', t('pwa.message.install_failed'))
    } finally {
      isInstalling.value = false
    }
    return
  }

  // Si instructions manuelles disponibles (Safari/Firefox)
  if (store.canShowManualInstructions) {
    showManualInstructions.value = true
    return
  }

  // Fallback: recharger pour mise à jour de version
  if (store.installType === 'version_update') {
    window.location.reload()
  }
}

const handleDismiss = () => {
  store.dismiss()
  showToastMessage('warning', t('pwa.message.reminder_later'), 2000)
}

const closeManualInstructions = () => {
  showManualInstructions.value = false
  store.dismiss()
}

// Lifecycle
onMounted(() => {
  // Initialiser le store avec la version de l'app
  const config = useRuntimeConfig()
  store.init(config.public.appVersion || '1.0.0')

  console.log('=== PWA Debug ===')
  console.log('Protocol:', window.location.protocol)
  console.log('Hostname:', window.location.hostname)
  console.log('Standalone:', window.matchMedia('(display-mode: standalone)').matches)

  // Vérifier le SW
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then(reg => {
      console.log('SW Registration:', reg)
    })
  }

  // Forcer un test après 2 secondes
  setTimeout(() => {
    console.log('deferredPrompt après 2s:', store.deferredPrompt)
    console.log('Store state:', store.getDebugInfo())
  }, 2000)
})

// Exposer pour le parent si nécessaire
defineExpose({
  showToastMessage
})
</script>

<style scoped>
/* Animations */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>